import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
// Import API config to ensure axios.defaults.baseURL is set
import '../config/api';

interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  role: string;
  tenantId?: number;
  tenantName?: string;
  tenantCode?: string;
  timezone?: string;
}

interface LoginResult {
  mustChangePassword: boolean;
  mfaRequired?: boolean;
  mfaToken?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  loginWithToken: (token: string, user: User) => void;
  register: (username: string, password: string, fullName: string, email?: string, role?: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  mustChangePassword: boolean;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Load token and user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Set default Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      // Validate token with backend
      validateToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async (tokenToValidate: string) => {
    try {
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${tokenToValidate}` }
      });
      setUser(response.data);
      setLoading(false);
    } catch (error: any) {
      console.error('Token validation failed:', error);
      // Only clear token if it's explicitly an authentication error (401 or 403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      // Use fetch API directly to ensure POST method is used
      // Always use relative URLs - CRA proxy handles dev, CloudFront/nginx handles production
      // This ensures CSRF cookies (XSRF-TOKEN) are same-origin and accessible
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      console.log('Attempting login to:', `${apiUrl}/api/auth/login`);
      
      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include credentials for CORS
        redirect: 'manual', // Don't follow redirects automatically
        body: JSON.stringify({
          username,
          password
        })
      }).catch((error) => {
        // Network error (CORS, connection refused, etc.)
        console.error('Network error during login:', error);
        throw new Error(`Failed to connect to backend: ${error.message}. Make sure the backend is running on ${apiUrl}`);
      });

      // Handle redirects (302/301) - these shouldn't happen but if they do, treat as error
      if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 301) {
        throw new Error('Unexpected redirect during login');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Login failed with status ${response.status}` }));
        throw new Error(errorData.error || `Login failed with status ${response.status}`);
      }

      const responseData = await response.json();

      // Check if MFA is required
      if (responseData.mfaRequired) {
        return {
          mustChangePassword: false,
          mfaRequired: true,
          mfaToken: responseData.mfaToken
        };
      }

      const { token: newToken, user: userData, mustChangePassword: needsPasswordChange } = responseData;
      
      setToken(newToken);
      setUser(userData);
      setMustChangePassword(!!needsPasswordChange);
      
      // Store in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      if (needsPasswordChange) {
        localStorage.setItem('mustChangePassword', 'true');
      }
      
      // Set default Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { mustChangePassword: !!needsPasswordChange, mfaRequired: false };
    } catch (error: any) {
      // Error is already a string from fetch error handling, or use message
      throw new Error(error.message || 'Login failed');
    }
  };
  
  // Login with an existing token (used after MFA verification)
  const loginWithToken = (newToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);
    
    // Store in localStorage
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Set default Authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };
  
  const clearMustChangePassword = () => {
    setMustChangePassword(false);
    localStorage.removeItem('mustChangePassword');
  };

  const register = async (username: string, password: string, fullName: string, email?: string, role?: string) => {
    try {
      const response = await axios.post('/api/auth/register', {
        username,
        password,
        fullName,
        email,
        role
      });

      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      
      // Store in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set default Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    try { sessionStorage.removeItem('tenantConfig'); } catch {}
    delete axios.defaults.headers.common['Authorization'];
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithToken,
    register,
    logout,
    isAuthenticated: !!token && !!user,
    loading,
    mustChangePassword,
    clearMustChangePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

